import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { useEffect } from "react";
import { testSupabaseConnection, setupInitialAdmin } from "@/services/supabaseService";

// Pages
import Index from "./pages/Index";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import MessagesIndex from "./pages/MessagesIndex";
import NotFound from "./pages/NotFound";
import ChangePassword from "./pages/ChangePassword";

// Content Management Pages
import Slides from "./pages/content/home/Slides";
import Services from "./pages/content/home/Services";
import Projects from "./pages/content/home/Projects";
import PortfolioContent from "./pages/content/portfolio/Content";
import ServicesContent from "./pages/content/services/Content";
import ServiceCategories from "./pages/content/services/Categories";
import AboutContent from "./pages/content/about/Content";
import ExpertiseAreas from "./pages/content/about/ExpertiseAreas";
import NewExpertiseArea from "./pages/content/about/expertise/NewExpertiseArea";
import EditExpertiseArea from "./pages/content/about/expertise/EditExpertiseArea";

// New Content Pages
import NewSlide from "./pages/content/home/slides/NewSlide";
import NewService from "./pages/content/home/services/NewService";
import NewHomeProject from "./pages/content/home/projects/NewProject";

// Portfolio Pages
import PortfolioCategories from "./pages/portfolio/Categories";
import PortfolioProjects from "./pages/portfolio/Projects";
import NewProject from "./pages/portfolio/projects/NewProject";
import EditProject from "./pages/portfolio/projects/EditProject";
import NewCategory from "./pages/portfolio/categories/NewCategory";
import EditCategory from "./pages/portfolio/categories/EditCategory";

// Service Categories Pages
import NewServiceCategory from "./pages/content/services/categories/NewServiceCategory";
import EditSlide from "./pages/content/home/slides/EditSlide";
import EditHomeProject from "@/pages/content/home/projects/EditProject";
import EditService from "@/pages/content/home/services/EditService";
import EditServiceCategory from "@/pages/content/services/categories/EditServiceCategory";

const queryClient = new QueryClient();

const App = () => {
  useEffect(() => {
    // Supabase bağlantısını kontrol et
    testSupabaseConnection()
      .then((isConnected) => {
        // Eğer bağlantı başarılıysa, admin kullanıcısının kurulumunu yap
        if (isConnected) {
          setupInitialAdmin();
        }
      });
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider defaultTheme="system" storageKey="trio-nexus-theme">
          <AuthProvider>
            <Toaster />
            <Sonner />
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/login" element={<Login />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/messages" element={<MessagesIndex />} />
              <Route path="/change-password" element={<ChangePassword />} />
              
              {/* Content management routes */}
              <Route path="/content/home/slides" element={<Slides />} />
              <Route path="/content/home/slides/new" element={<NewSlide />} />
              <Route path="/content/home/slides/:id/edit" element={<EditSlide />} />
              <Route path="/content/home/services" element={<Services />} />
              <Route path="/content/home/services/new" element={<NewService />} />
              <Route path="/content/home/services/:id/edit" element={<EditService />} />
              <Route path="/content/home/projects" element={<Projects />} />
              <Route path="/content/home/projects/new" element={<NewHomeProject />} />
              <Route path="/content/home/projects/:id/edit" element={<EditHomeProject />} />
              <Route path="/content/portfolio/content" element={<PortfolioContent />} />
              <Route path="/content/services/content" element={<ServicesContent />} />
              <Route path="/content/services/categories" element={<ServiceCategories />} />
              <Route path="/content/services/categories/new" element={<NewServiceCategory />} />
              <Route path="/content/services/categories/:id/edit" element={<EditServiceCategory />} />
              <Route path="/content/about/content" element={<AboutContent />} />
              <Route path="/content/about/expertise" element={<ExpertiseAreas />} />
              <Route path="/content/about/expertise/new" element={<NewExpertiseArea />} />
              <Route path="/content/about/expertise/:id/edit" element={<EditExpertiseArea />} />
              
              {/* Portfolio routes */}
              <Route path="/portfolio/categories" element={<PortfolioCategories />} />
              <Route path="/portfolio/categories/new" element={<NewCategory />} />
              <Route path="/portfolio/categories/:id/edit" element={<EditCategory />} />
              <Route path="/portfolio/projects" element={<PortfolioProjects />} />
              <Route path="/portfolio/projects/new" element={<NewProject />} />
              <Route path="/portfolio/projects/:id/edit" element={<EditProject />} />
              
              <Route path="*" element={<NotFound />} />
            </Routes>
          </AuthProvider>
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;
